import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import type { AssignableRep } from '../board/leads-board.component';
import { StageCatalogEntry, stageLabelOf } from '../../../shared/stage-catalog';
import {
  ROLLOFF_OPPORTUNITIES_URL, RollOffOpportunity, RollOffOpportunityDetail,
  RollOffStageHistoryEntry,
} from './rolloff-opportunity.model';
import { rollOffStageColor } from './rolloff-stage-colors';
import { formatHireDate } from './rolloff-board.component';

/**
 * One hire, and how it got where it is.
 *
 * The trail is the point of this detail page. A roll-off hire moves along a branch
 * and every step carries a note that is often the only record of why — a quote
 * lost to a competitor's price, a hire pushed back a week — so the history is
 * shown in full rather than summarised, newest first, exactly as the server
 * ordered it (`ListHistoryAsync` orders by `changed_at` descending and takes
 * the newest N). Re-sorting it here would be a second opinion about ordering
 * that could only ever disagree with the server's.
 */
@Component({
  selector: 'app-rolloff-hire-detail',
  templateUrl: './rolloff-hire-detail.component.html',
  styleUrls: ['./rolloff-hire-detail.component.css'],
  host: { class: 'workspace-page', '[class.workspace-page--full]': '!inline' },
})
export class RollOffHireDetailComponent implements OnChanges, OnDestroy {
  /** The hire selected by the child route; null closes the detail pane. */
  @Input() hireId: number | null = null;
  @Input() inline = false;
  private request?: Subscription;

  /** The board's catalog, so a stage prints the same wording in both places. */
  @Input() stages: StageCatalogEntry[] = [];

  @Input() reps: AssignableRep[] = [];

  @Input() canManage = false;

  @Output() closed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<RollOffOpportunity>();

  detail: RollOffOpportunityDetail | null = null;
  loading = false;
  loadError: string | null = null;

  constructor(private http: HttpClient) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('hireId' in changes) this.load();
  }

  ngOnDestroy(): void { this.request?.unsubscribe(); }

  load(): void {
    this.request?.unsubscribe();
    this.detail = null;
    this.loadError = null;
    this.loading = false;
    if (this.hireId == null) return;

    this.loading = true;
    this.request = this.http.get<RollOffOpportunityDetail>(
      `${ROLLOFF_OPPORTUNITIES_URL}/${this.hireId}`).subscribe({
      next: detail => {
        this.loading = false;
        this.detail = detail;
      },
      error: (err: { status?: number }) => {
        this.loading = false;
        this.loadError = err?.status === 404
          ? 'That deal is no longer there.'
          : `Failed to load this hire (HTTP ${err?.status ?? '?'}).`;
      },
    });
  }

  close(): void { this.closed.emit(); }

  editHire(): void {
    if (this.detail) this.edit.emit(this.detail.opportunity);
  }

  /** The catalog's wording where we have it, and the hire's own where we do
   *  not — a stage the tenant has since retired still names itself. */
  stageLabel(code: string, fallback: string): string {
    return this.stages.length ? stageLabelOf(this.stages, code) : fallback;
  }

  stageColor(code: string): string { return rollOffStageColor(code); }

  /** Whether the hire is finished, asked of the outcome. This vocabulary's
   *  winning stage is called Fulfillment, so recognising "Won" finds nothing. */
  get finished(): boolean {
    return !!this.detail && this.detail.opportunity.leadStageOutcome !== 'open';
  }

  ownerLabel(hire: RollOffOpportunity): string {
    if (hire.salesRepUserId == null) return 'Unassigned';
    const rep = this.reps.find(r => r.userId === hire.salesRepUserId);
    return rep?.displayName?.trim() || rep?.userName?.trim() || `Owner ${hire.salesRepUserId}`;
  }

  /** Who moved it. An automated actor has no user id, and says so rather than
   *  borrowing a person's name. */
  actorLabel(entry: RollOffStageHistoryEntry): string {
    if (entry.changedByActorId == null) return entry.changedByActorType;
    const rep = this.reps.find(r => r.userId === entry.changedByActorId);
    const name = rep?.displayName?.trim() || rep?.userName?.trim();
    return name ?? `${entry.changedByActorType} ${entry.changedByActorId}`;
  }

  hireDate(value: string | null): string | null { return formatHireDate(value); }

  hireWindow(hire: RollOffOpportunity): string {
    const from = formatHireDate(hire.hireStartsOn);
    const to = formatHireDate(hire.hireEndsOn);
    if (from && to) return `${from} – ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Until ${to}`;
    return '—';
  }

  siteAddress(hire: RollOffOpportunity): string {
    return [hire.addressLine1, hire.city, hire.postalCode]
      .filter(part => !!part && part.trim()).join(', ') || '—';
  }

  /**
   * The catalog's wording for this hire's size, or — where the 2026-09-03
   * backfill could not place what a rep had typed — that phrase.
   *
   * Same rule as the board's card, deliberately the same two lines rather than
   * a shared helper: the two would have to agree anyway, and a detail page that
   * showed a size the card beside it does not is worse than the duplication.
   */
  containerLabel(hire: RollOffOpportunity): string | null {
    const label = hire.containerTypeLabel?.trim();
    if (label) return label;
    const typed = hire.containerSizeText?.trim();
    return typed ? typed : null;
  }

  /** Whether that string is a phrase somebody typed rather than a catalog row. */
  isUnplacedSize(hire: RollOffOpportunity): boolean {
    return hire.containerTypeId == null && !!hire.containerSizeText?.trim();
  }

  trackByEntry = (index: number, entry: RollOffStageHistoryEntry): string =>
    `${index}-${entry.changedAt}-${entry.toLeadStageId}`;
}
